import { Injectable } from '@angular/core';
import { Subject, Observable, asyncScheduler } from 'rxjs';
import { distinctUntilChanged, filter, throttleTime } from 'rxjs/operators';

// ─── ErrorSink ────────────────────────────────────────────────────────────────
//
// Centralized error reporting service. All modbus/communication errors flow
// here. A single subscriber (root component) decides how to display them.
//
// Built-in deduplication via RxJS: identical errors within a debounce window
// are collapsed into one. This replaces the ToastFacade dedup hack.
//
// Usage:
//   // Report an error:
//   errorSink.report({ code: 'TRANSPORT_TIMEOUT', message: '...', severity: 'toast' });
//
//   // Subscribe in root component (operators/app.component, customer-portal/app.component):
//   errorSink.toasts$.subscribe(err => toastFacade.present(err.message, err.title, ...));
//
// Dedup pipeline:
//   throttleTime(300ms, leading+trailing) → distinctUntilChanged by source
//   Effect: 7 transport errors from 7 sections → 1 toast (same source).
//   A transport error followed by a protocol error → 2 toasts (different sources).

export type ErrorSeverity = 'silent' | 'toast' | 'blocking';

export type ErrorSource = 'transport' | 'protocol' | 'application';

export interface DisplayableError {
  /** Unique error code for deduplication (e.g. 'TRANSPORT_TIMEOUT', 'PROTOCOL_0x02'). */
  readonly code: string;
  /** User-facing message (i18n key or translated string). */
  readonly message: string;
  /** Optional title for the toast/dialog. */
  readonly title?: string;
  /** How to display this error. */
  readonly severity: ErrorSeverity;
  /** Error origin for logging/filtering. */
  readonly source: ErrorSource;
  /** Technical detail (for console/Sentry, not shown to user). */
  readonly technicalDetail?: string;
  /** Timestamp. */
  readonly timestamp: number;
}

/** Debounce window for deduplication (ms). */
const DEDUP_DEBOUNCE_MS = 300;

@Injectable({
  providedIn: 'root',
})
export class ErrorSinkService {
  private readonly errors$ = new Subject<DisplayableError>();

  /** All errors (including silent). Subscribe for logging/telemetry. */
  readonly all$: Observable<DisplayableError> = this.errors$.asObservable();

  /**
   * Errors that should be shown as toast.
   * Deduplicated by source (transport / protocol / application):
   *  - throttleTime shows the first error immediately, then rate-limits the burst.
   *  - distinctUntilChanged collapses consecutive errors from the same source.
   *
   * This means: 7 transport errors from 7 sections → 1 toast (same source).
   * A transport error followed by a protocol error → 2 toasts (different sources).
   */
  readonly toasts$: Observable<DisplayableError> = this.errors$.pipe(
    filter(e => e.severity === 'toast'),
    throttleTime(DEDUP_DEBOUNCE_MS, asyncScheduler, { leading: true, trailing: true }),
    distinctUntilChanged((a, b) => a.source === b.source),
  );

  /**
   * Errors that should block the UI (modal dialog).
   * Not deduplicated — each blocking error requires user action.
   */
  readonly blocking$: Observable<DisplayableError> = this.errors$.pipe(
    filter(e => e.severity === 'blocking'),
  );

  /**
   * Report an error to the sink.
   * Silent errors are logged but not displayed.
   * Toast errors are deduplicated and shown to the user.
   * Blocking errors show a modal dialog.
   */
  report(error: DisplayableError): void {
    if (error.severity !== 'silent') {
      console.log(`[ErrorSink] ${error.severity.toUpperCase()} [${error.code}] ${error.message}`, error.technicalDetail ?? '');
    }
    this.errors$.next(error);
  }

  /**
   * Convenience: report a toast error from a DeviceError.
   */
  reportTransport(message: string, detail?: string): void {
    this.report({
      code: 'TRANSPORT_ERROR',
      message,
      severity: 'toast',
      source: 'transport',
      technicalDetail: detail,
      timestamp: Date.now(),
    });
  }

  /**
   * Convenience: report a silent error (logged, not shown).
   */
  reportSilent(code: string, message: string, detail?: string): void {
    this.report({
      code,
      message,
      severity: 'silent',
      source: 'protocol',
      technicalDetail: detail,
      timestamp: Date.now(),
    });
  }
}
